import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
} from '@chakra-ui/react';
import { Table } from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useCallback } from 'react';
import { CSVLink } from 'react-csv';
import { utils, writeFile } from 'xlsx';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import { Icon } from '~/packages/common-frontend/components/icons';

export const getTanstackVisibleDataForDownload = ({
  tableInstance,
  excludeColumnIds = [],
  adjustValueFnMap = {},
}: {
  tableInstance: Table<any>;
  excludeColumnIds?: string[];
  adjustValueFnMap?: { [header: string]: (value: any) => any };
}) => {
  const dataRows = tableInstance.getSortedRowModel().rows.map((row) => {
    const dataRow: { [key: string]: any } = {};

    row.getVisibleCells().forEach((cell) => {
      const id = cell.column.columnDef?.id;
      // if the column doesn't have an id, add it to the data row
      // if there's an id and it's not excluded, add it to the data row
      if (!id || !excludeColumnIds.includes(id)) {
        const header =
          cell.column.columnDef.header?.toString() ?? cell.column.id;

        const value = cell.getValue();
        const adjustValueFn = adjustValueFnMap[header];
        dataRow[header] = adjustValueFn ? adjustValueFn(value) : value;
      }
    });

    return dataRow;
  });

  const headers = Object.keys(dataRows[0] ?? {}).map((headerKey) => {
    return {
      label: commonUtils.toTitleCase(headerKey),
      key: headerKey,
    };
  });

  return { dataRows, headers };
};

export interface CsvDownloadLinkProps {
  linkText?: string;
  dataRows: { [key: string]: any }[];
  headers: { label: string; key: any }[] | string[];
  filenamePrefix: string;
  MenuButtonProps?: React.ComponentProps<typeof MenuButton>;
  returnMenuItemsOnly?: boolean;
}
export const CsvXlsxDownloadSelect = ({
  linkText = 'Download',
  dataRows,
  headers,
  filenamePrefix,
  MenuButtonProps,
  returnMenuItemsOnly,
}: CsvDownloadLinkProps) => {
  const todayFullDateStr = commonDate.currentDateStr();
  const filename = `${_.snakeCase(filenamePrefix)}-${todayFullDateStr}.csv`;

  const exportToXlsx = useCallback(() => {
    const ws = utils.json_to_sheet(dataRows);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Data export');
    writeFile(wb, `${filenamePrefix}-${todayFullDateStr}.xlsx`);
  }, [dataRows, filenamePrefix, todayFullDateStr]);

  const MenuItems = (
    <>
      <MenuItem>
        {/* !TODO @eldarshamukhmedov react-csv is no longer maintained, needs to be replaced */}

        {/* @timmydoza - width 100% makes it so you can now click anywhere in the MenuItem to
            begin a download. Without it, you had to click only on the "Download CSV" text. */}
        <CSVLink
          data={dataRows}
          headers={headers}
          filename={filename}
          style={{ width: '100%' }}
        >
          <Text>Download CSV</Text>
        </CSVLink>
      </MenuItem>
      <MenuItem onClick={exportToXlsx}>Download XLSX</MenuItem>
    </>
  );

  if (returnMenuItemsOnly) {
    return MenuItems;
  }

  return (
    <Box style={{ alignSelf: 'self-end' }}>
      <Menu>
        <MenuButton
          as={Button}
          variant="secondary-muted"
          colorScheme="gray"
          width="fit-content"
          leftIcon={<Icon icon="arrow-down-to-bracket" />}
          {...MenuButtonProps}
        >
          {linkText}
        </MenuButton>
        <MenuList zIndex={100}>{MenuItems}</MenuList>
      </Menu>
    </Box>
  );
};
